import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import apiClient from 'utils/apiClient';

export interface TeamSearchParams {
    page?: number;
    perPage?: number;
    totalSearchKeyword?: string;
    id?: string;
    idSearchType?: string;
    name?: string;
    nameSearchType?: string;
    domain?: string;
    domainSearchType?: string;
    groupIds?: string[];
    groupDomains?: string[];
    status?: string[];
    pricing?: string[];
    subPricing?: string[];
    teamTag?: string[];
    integration?: string[];
    country?: string;
    isTestTeam?: string;
    blocked?: string;
    gteMessageUsage?: number;
    lteMessageUsage?: number;
    gteConnectUsage?: number;
    lteConnectUsage?: number;
    gteFileUsage?: number;
    lteFileUsage?: number;
    gteAlbumUsage?: number;
    lteAlbumUsage?: number;
    gteDriveUsage?: number;
    lteDriveUsage?: number;
    gteMemberCount?: number;
    lteMemberCount?: number;
    gteAssociateCount?: number;
    lteAssociateCount?: number;
    gteTeamRecentDauCount?: number;
    lteTeamRecentDauCount?: number;
    createdStartDateTime?: string;
    createdEndDateTime?: string;
    lastSeenStartDateTime?: string;
    lastSeenEndDateTime?: string;
    orderTarget?: string;
    orderType?: string;
}

export interface TeamItem {
    id: number;
    name: string;
    domain: string;
    status: string;
    country: string;
    createdAt: string;
    updatedAt: string;
    lastSeen: string | null;
    logoUrl: string;
    usage: {
        messageCount: number; connectCount: number;
        fileSize: number; albumFileSize: number; driveFileSize: number;
    } | null;
    plan: {
        pricing: string; subPricing: string; subPricingGroup: string;
        fileSize: number; isTestTeam: boolean; blocked: boolean;
    } | null;
    memberCount: { member: number; associate: number } | null;
    teamRecentDauCount: { dau: number } | null;
}

export interface TeamState {
    teams: TeamItem[];
    totalCount: number;
    page: number;
    perPage: number;
    loading: boolean;
}

export const initialState: TeamState = {
    teams: [],
    totalCount: 0,
    page: 1,
    perPage: 50,
    loading: false,
};

export const searchTeams = createAsyncThunk<
    { list: TeamItem[]; totalCount: number; page: number; perPage: number },
    TeamSearchParams,
    { state: RootState }
>(
    'team/search',
    async (params, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) value.forEach(v => queryParams.append(key, String(v)));
                    else queryParams.append(key, String(value));
                }
            });
            const response = await apiClient.get(`/api/teams/search?${queryParams.toString()}`);
            return response.data;
        } catch (e: any) {
            return rejectWithValue(e?.response?.data?.message || 'Failed to search teams');
        }
    },
    {
        condition: (_, { getState }) => {
            const { loading } = (getState() as RootState).team as TeamState;
            return !loading;
        }
    }
);

const teamSlice = createSlice({
    name: 'team',
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(searchTeams.pending, (state) => { state.loading = true; })
            .addCase(searchTeams.fulfilled, (state, action) => {
                state.loading = false;
                const incomingPage = action.payload.page;
                if (incomingPage === 1) state.teams = action.payload.list;
                else if (incomingPage === state.page + 1) state.teams = [...state.teams, ...action.payload.list];
                else return;
                state.totalCount = action.payload.totalCount;
                state.page = incomingPage;
                state.perPage = action.payload.perPage;
            })
            .addCase(searchTeams.rejected, (state) => { state.loading = false; });
    },
    reducers: {
        resetTeam: () => initialState,
    }
});

export const { resetTeam } = teamSlice.actions;
export default teamSlice.reducer;
